import { Box, Container, createTheme, GlobalStyles, Grid, Paper, ThemeProvider, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useRef } from 'react';

const WaveCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let time = 0;
    let animId: number;

    const NUM_LINES = 18;
    const DOT_SPACING = 7;
    const BAND_HEIGHT = 130;
    const SPEED = 0.006;

    const waveY = (x: number, baseY: number, phase: number) => {
      const a = Math.sin(x * 0.006 - time + phase) * 36;
      const b = Math.sin(x * 0.0025 - time * 0.6 + phase) * 22;
      return baseY + a + b;
    };

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    window.addEventListener('resize', resize);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const centerY = canvas.height * 0.52;

      for (let l = 0; l < NUM_LINES; l++) {
        const t = l / (NUM_LINES - 1);
        const baseY = centerY - BAND_HEIGHT / 2 + t * BAND_HEIGHT;
        const phase = l * 0.22;
        const dist = Math.abs(t - 0.5) * 2;
        const opacity = 0.15 + (1 - dist) * 0.48;
        const size = 0.9 + (1 - dist) * 0.9;

        for (let x = 0; x <= canvas.width; x += DOT_SPACING) {
          const y = waveY(x, baseY, phase);
          const edgeFade = Math.min(1, Math.min(x / 140, (canvas.width - x) / 140));
          const finalAlpha = opacity * edgeFade;
          if (finalAlpha < 0.01) continue;

          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(1, 235, 122, ${finalAlpha})`;
          ctx.fill();
        }
      }

      time += SPEED;
      animId = requestAnimationFrame(draw);
    };

    animId = requestAnimationFrame(() => {
      resize();
      draw();
    });
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }} />;
};

const loginTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#01EB7A',
      light: '#99F7CA',
      dark: '#0D3D2A',
      contrastText: '#000000',
    },
    secondary: {
      main: '#FFFFFF',
      contrastText: '#042222',
    },
    background: {
      default: '#042222',
      paper: '#0B2D2D',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#F2FEF8',
    },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '28px',
          fontFamily: '"Familjen Grotesk", sans-serif',
          fontWeight: 700,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
      },
    },
  },
});

export const Login = ({ children }) => {
  function changeLogo() {
    const matches = useMediaQuery('(min-width:899px)');
    const img = '../../../content/images/logo_2026_sigaway.svg';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <img
          src={img}
          className="logo-sigaway"
          alt="Logomarca Sigaway"
          style={{
            height: matches ? '52px' : '24px',
          }}
        />
        <span
          style={{
            color: loginTheme.palette.primary.main,
            fontFamily: '"Familjen Grotesk", sans-serif',
            fontWeight: 700,
            fontSize: matches ? '21px' : '13px',
            letterSpacing: '0.05em',
            textAlign: 'center',
          }}
        >
          Cresça sem imprevistos
        </span>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={loginTheme}>
      <GlobalStyles
        styles={`@import url('https://fonts.googleapis.com/css2?family=Familjen+Grotesk:wght@400;700&family=Plus+Jakarta+Sans:wght@400;700&display=swap');`}
      />
      <GlobalStyles
        styles={theme => ({
          '@keyframes breathe': {
            '0%, 100%': { opacity: 0.6, transform: 'translate(-50%, -50%) scale(1)' },
            '50%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1.2)' },
          },
          'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active': {
            WebkitBoxShadow: `0 0 0px 1000px ${theme.palette.primary.dark} inset !important`,
            WebkitTextFillColor: `${theme.palette.text.primary} !important`,
            caretColor: theme.palette.text.primary,
          },
        })}
      />
      <Grid container component="div" sx={{ height: '100dvh', backgroundColor: 'background.default' }}>
        <Grid
          item
          xs={false}
          md={5}
          sx={{
            display: { xs: 'none', md: 'flex' },
            backgroundImage: 'url(../../../content/images/background_truck_em_cima_otimizado.webp)',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: theme => alpha(theme.palette.background.default, 0.35),
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>{changeLogo()}</Box>
        </Grid>
        <Grid
          container
          item
          xs={12}
          md={7}
          sx={{
            backgroundColor: 'background.default',
            alignContent: 'center',
            height: '100dvh',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <WaveCanvas />
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              position: 'absolute',
              top: '28px',
              left: '28px',
              zIndex: 2,
            }}
          >
            <img src="../../../content/images/logo_2026_sigaway.svg" className="logo-sigaway" alt="Logomarca Sigaway" style={{ height: '28px' }} />
          </Box>
          <Container component="main" maxWidth="sm" sx={{ mb: 4, position: 'relative', zIndex: 1 }}>
            <Paper
              elevation={0}
              sx={{
                px: { xs: 4, md: 6 },
                py: { xs: 2, md: 3 },
                borderRadius: '24px',
                backgroundColor: theme => alpha(theme.palette.background.paper, 0.85),
                border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                backdropFilter: 'blur(20px)',
              }}
            >
              {children}
            </Paper>
          </Container>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};
